// src/pages/Products.js
import React, { useEffect, useState, useRef } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Table, Spin, Pagination, Input, Button, Switch, Modal } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { useSearchBehavior } from '../hooks/useSearchBehavior';
import { useUpdateProductStatus, GET_PRODUCTS, GET_PRODUCT_BY_ID } from '../mutations/products';
import ProductModalForm from '../components/products/ProductModalForm';
import ProductImportModal from '../components/ProductImportModal';
import AdvancedProductFilterModal from '../components/products/AdvancedProductFilterModal';
import ActiveAdvancedProductFilters from '../components/products/ActiveAdvancedProductFilters';

const Products = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Usa o hook customizado para gerenciar o comportamento de busca
  const {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    handleManualSearch,
    clearSearch,
    isInstantSearch
  } = useSearchBehavior("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isAdvancedFilterModalVisible, setIsAdvancedFilterModalVisible] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const searchInputRef = useRef(null);
  const [updateProductStatus] = useUpdateProductStatus();

  // Remove o useEffect do debounce, agora é gerenciado pelo hook customizado

  const { loading, error, data, refetch } = useQuery(GET_PRODUCTS, {
    variables: { 
      page: currentPage, 
      perPage: pageSize, 
      search: activeSearchTerm,
      advancedFilters: advancedFilters.map(filter => ({
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
        fieldType: filter.fieldType
      }))
    },
    fetchPolicy: 'network-only',
  });

  const [getProductById, { data: productData }] = useLazyQuery(GET_PRODUCT_BY_ID);

  const handleSearch = () => {
    if (!isInstantSearch) {
      handleManualSearch();
    }
    setCurrentPage(1); // Reset page to 1 when search term changes
  };

  const showConfirmModal = (product) => {
    setSelectedProduct(product);
    setIsModalVisible(true);
  };

  const handleImport = () => {
    setIsImportModalVisible(true);
  };

  const handleImportCancel = () => {
    setIsImportModalVisible(false);
  };

  const handleOk = async () => {
    setIsModalVisible(false);
    const { id, active } = selectedProduct;
    try {
      await updateProductStatus({
        variables: { id, active: !active },
        update: (cache, { data: { updateProductStatus } }) => {
          const { product } = updateProductStatus;
          cache.modify({
            id: cache.identify(product),
            fields: {
              active(cachedActive) {
                return !active;
              }
            }
          });
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setIsFormModalVisible(false);
    setIsModalVisible(false);
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setIsEditMode(false);
  };

  const handleCreateProduct = () => {
    resetForm();
    setIsFormModalVisible(true);
  };

  const handleEditProduct = (product) => {
    setIsEditMode(true);
    getProductById({ variables: { id: product.id } });
    setIsFormModalVisible(true);
  };

  const showAdvancedFilterModal = () => {
    setIsAdvancedFilterModalVisible(true);
  };

  const handleAdvancedFilterCancel = () => {
    setIsAdvancedFilterModalVisible(false);
  };

  const handleAdvancedFilterApply = (filters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1); // Reset para primeira página quando aplicar filtros
    setIsAdvancedFilterModalVisible(false);
  };

  const getActiveAdvancedFiltersCount = () => {
    return advancedFilters.length;
  };

  const handleRemoveAdvancedFilter = (filterIndex) => {
    const newFilters = advancedFilters.filter((_, index) => index !== filterIndex);
    setAdvancedFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearAllAdvancedFilters = () => {
    setAdvancedFilters([]);
    setCurrentPage(1);
  };

  if (error) return <p>Error: {error.message}</p>;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Código',
      dataIndex: 'reference',
      key: 'reference',
      align: 'center',
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Marca',
      dataIndex: 'brand',
      key: 'brand',
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Grupo',
      dataIndex: ['group', 'name'],
      key: 'group',
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: (text) => format(new Date(text), 'dd/MM/yyyy - HH:mm'),
    },
    {
      title: 'Ativo',
      dataIndex: 'active',
      key: 'active',
      align: 'center',
      fixed: 'right',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => showConfirmModal(record)}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (text, record) => (
        <Button type="link" onClick={() => handleEditProduct(record)}>Editar</Button>
      ),
    },
  ];

  const handleTableChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const products = data?.products?.edges.map(edge => edge.node) || [];
  const { totalCount } = data?.products || { pageInfo: {}, totalCount: 0 };

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Input
          ref={searchInputRef}
          placeholder={isInstantSearch ? "Pesquisar produtos (busca automática)" : "Pesquisar produtos"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: 8, width: 300 }}
          onPressEnter={handleSearch}
          autoFocus
          allowClear={true}
        />
        {!isInstantSearch && (
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Pesquisar
          </Button>
        )}
        <Button 
          type={getActiveAdvancedFiltersCount() > 0 ? "primary" : "default"}
          icon={<FilterOutlined />}
          onClick={showAdvancedFilterModal}
          style={{ marginLeft: 8 }}
        >
          Filtros Avançados
          {getActiveAdvancedFiltersCount() > 0 && (
            <span style={{ marginLeft: 4, backgroundColor: '#ff4d4f', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px' }}>
              {getActiveAdvancedFiltersCount()}
            </span>
          )}
        </Button>
        
        <Button type="primary" style={{ marginLeft: 'auto' }} onClick={handleCreateProduct}>
          Criar Produto
        </Button>
        <Button  style={{ marginLeft: '5px' }} onClick={handleImport}>
          Importar Produtos
        </Button>
      </div>
      <ActiveAdvancedProductFilters 
        filters={advancedFilters}
        onRemoveFilter={handleRemoveAdvancedFilter}
        onClearAll={handleClearAllAdvancedFilters}
      />
      <Spin spinning={loading}>
        <Table
          dataSource={products}
          columns={columns}
          rowKey="id"
          pagination={false}
        />
      </Spin>
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={totalCount}
        locale={{ items_per_page: '/ página' }}
        onChange={handleTableChange}
      />
      <Modal
        title="Confirmação"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        getContainer={false}
      >
        <p>Tem certeza que deseja continuar?</p>
      </Modal>
      <ProductModalForm
        visible={isFormModalVisible}
        onCancel={handleCancel}
        initialValues={isEditMode && productData ? productData.product : {}}
        isEditMode={isEditMode}
        refetchProducts={refetch}
        key={isEditMode ? selectedProduct?.id : 'new'} // Add key to force re-render
      />
      <ProductImportModal
        visible={isImportModalVisible}
        onCancel={handleImportCancel}
        refetchProducts={refetch}
        resetToFirstPage={() => setCurrentPage(1)}
      />
      <AdvancedProductFilterModal
        open={isAdvancedFilterModalVisible}
        onCancel={handleAdvancedFilterCancel}
        onApply={handleAdvancedFilterApply}
        initialFilters={advancedFilters}
      />
    </>
  );
};

export default Products;
